import {
  CheckCircle2,
  AlertCircle,
  X,
  RotateCcw,
  Loader2,
  Circle,
  MinusCircle,
  ShieldAlert,
} from 'lucide-react';
import FileTypeIcon from './FileTypeIcon';
import { IconButton } from '../common/Button';
import { formatBytes } from '../../utils/format';
import cn from '../../utils/cn';

const LABEL = {
  queued: 'Waiting…',
  uploading: 'Uploading',
  committing: 'Verifying and saving…',
  processing: 'Reading the document',
  done: 'Complete',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/**
 * One line per stage of the pipeline, with the state the server reports for it.
 *
 * Every row here is backed by a status column. Nothing is shown as finished because enough
 * time has passed — which is what the tray used to do, flipping to "Complete" 6.2 seconds
 * after the commit regardless of what had actually happened to the file.
 */
const STATE = {
  done: { icon: CheckCircle2, colour: 'var(--success)' },
  running: { icon: Loader2, colour: 'var(--ac)', spin: true },
  waiting: { icon: Circle, colour: 'var(--ink-3)' },
  skipped: { icon: MinusCircle, colour: 'var(--ink-3)' },
  blocked: { icon: ShieldAlert, colour: 'var(--warn-fg)' },
  failed: { icon: AlertCircle, colour: 'var(--err-fg)' },
};

/**
 * Label and detail on one line. The panel is wide enough for it, and a stacked pair per
 * stage turned seven steps into fourteen lines of mostly whitespace.
 */
function Stage({ label, state, detail }) {
  const { icon: Icon, colour, spin } = STATE[state] || STATE.waiting;
  const quiet = state === 'waiting' || state === 'skipped';
  return (
    <div className="row" style={{ gap: 7, alignItems: 'flex-start', padding: '1.5px 0' }}>
      <Icon size={12} className={spin ? 'spin' : undefined} style={{ color: colour, flex: 'none', marginTop: 2.5 }} />
      <span style={{ fontSize: 12, lineHeight: 1.45, minWidth: 0, color: quiet ? 'var(--ink-3)' : 'var(--ink-2)' }}>
        {label}
        {detail && <span className="muted"> — {detail}</span>}
      </span>
    </div>
  );
}

/**
 * One row of the upload queue.
 *
 * The steps are not behind a toggle. The point of showing them is that somebody watching an
 * upload can see what the system is doing to their file; a disclosure triangle puts that
 * one click away from everybody who did not already know it was there.
 */
export default function UploadItem({ item, onRetry, onCancel, onRemove, compact }) {
  const active = ['queued', 'uploading', 'committing'].includes(item.status);
  const pct = item.status === 'done' || item.status === 'processing' ? 100 : Math.round(item.progress);

  // The checks that run before any bytes are sent are worth stating, because they are the
  // ones people assume are missing. The server does them; the row now says so.
  const preflight = [
    {
      key: 'checked',
      label: 'Type and size checked',
      state: item.status === 'failed' && !item.checked ? 'failed' : item.checked ? 'done' : 'running',
      detail: item.checked ? 'Extension allowed, within the size limit, permitted in this folder' : null,
    },
    {
      key: 'uploaded',
      label: 'Uploaded',
      state: item.checked && pct >= 100 ? 'done' : item.checked ? 'running' : 'waiting',
      detail: item.checked && pct < 100 ? `${pct}%` : null,
    },
    {
      key: 'verified',
      label: 'Verified against what was declared',
      state: item.committed ? 'done' : pct >= 100 && item.checked ? 'running' : 'waiting',
      detail: item.committed ? 'Size matched, no duplicate name in this folder' : null,
    },
  ];

  const stages = [...preflight, ...(item.stages || [])];
  const showSteps = item.status !== 'cancelled' && (item.checked || item.status === 'failed') && !compact;

  return (
    <div className="upload-item" style={{ alignItems: 'flex-start' }}>
      <FileTypeIcon name={item.name} size={compact ? 'sm' : 'md'} />

      <div className="upload-item__body">
        <div className="upload-item__row">
          <span className="upload-item__name truncate" title={item.name}>
            {item.name}
          </span>
          <span className="meta tnum" style={{ marginLeft: 'auto', flex: 'none' }}>
            {item.status === 'uploading' ? `${pct}%` : formatBytes(item.size)}
          </span>
          <span
            className="meta row"
            style={{
              gap: 5,
              flex: 'none',
              whiteSpace: 'nowrap',
              color:
                item.status === 'failed' ? 'var(--danger)' : item.status === 'done' ? 'var(--success)' : undefined,
            }}
          >
            {item.status === 'done' && <CheckCircle2 size={12} />}
            {item.status === 'failed' && <AlertCircle size={12} />}
            {(item.status === 'committing' || item.status === 'queued' || item.status === 'processing') && (
              <Loader2 size={12} className="spin" />
            )}
            {LABEL[item.status]}
          </span>
        </div>

        {(active || item.status === 'processing') && (
          <div className="progress">
            <div
              className={cn('progress__bar', item.status === 'processing' && 'progress__bar--success')}
              style={{ width: `${pct}%` }}
            />
          </div>
        )}

        {item.status === 'failed' && (
          <span className="meta" style={{ color: 'var(--danger)' }}>
            {item.error || 'Upload failed.'}
          </span>
        )}

        {showSteps && (
          <div className="stack" style={{ gap: 0, marginTop: 2, paddingLeft: 2 }}>
            {stages.map((s) => (
              <Stage key={s.key || s.label} label={s.label} state={s.state} detail={s.detail} />
            ))}
            {item.stalled && (
              <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>
                Still working after three minutes. The document is saved — reload the page to check again.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="row" style={{ gap: 2, flex: 'none' }}>
        {item.status === 'failed' && <IconButton icon={RotateCcw} label="Retry upload" size={15} onClick={() => onRetry(item.id)} />}
        {active && <IconButton icon={X} label="Cancel upload" size={15} onClick={() => onCancel(item.id)} />}
        {(item.status === 'done' || item.status === 'cancelled' || item.status === 'failed') && onRemove && (
          <IconButton icon={X} label="Remove from list" size={15} onClick={() => onRemove(item.id)} />
        )}
      </div>
    </div>
  );
}
